import { createPatientCartMutation } from '@/api/carts/adminPatientCartMutations';
import { getAllClinicsQuery } from '@/api/clinics/clinicQueries';
import { createTempCustomerMutation } from '@/api/temp-orders/tempOrderMutations';
import { getAllUsersQuery } from '@/api/users/usersQueries';
import { Button } from '@/components/common/Button';
import { Combobox } from '@/components/common/Combobox';
import { Input } from '@/components/common/Input';
import { PhoneInput } from '@/components/common/PhoneInput';
import { DashboardTableAccordion } from '@/components/dashboard/DashboardTableAccordion';
import { useFormMutation } from '@/hooks/useFormMutation';
import { routes } from '@/routes/routes';
import type { CreateWalkInOrderFormValues } from '@/validation/walkInOrderValidator';
import { createWalkInOrderSchema } from '@/validation/walkInOrderValidator';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useFormik } from 'formik';
import { toFormikValidate } from 'zod-formik-adapter';

const ROW_VALUE = 'create-walk-in';

interface CreateWalkInOrderRowProps {
  accordionOpenValue?: string;
}

/**
 * Orders-page accordion row that starts the walk-in PAID order flow: capture the
 * customer + doctor + clinic, create a temp customer and a cart, then hand off
 * to the subscription items page (in walk-in mode) to add items + create the
 * Paid order.
 */
export const CreateWalkInOrderRow = ({
  accordionOpenValue,
}: CreateWalkInOrderRowProps) => {
  const navigate = useNavigate();
  const isOpen = accordionOpenValue === ROW_VALUE;

  const { data: doctors = [] } = useQuery({
    ...getAllUsersQuery({ role: 'Doctor' }),
    enabled: isOpen,
  });
  const { data: clinics = [] } = useQuery({
    ...getAllClinicsQuery(),
    enabled: isOpen,
  });

  const doctorItems = doctors.map((doctor) => ({
    id: doctor.id,
    value: `${doctor.name} ${doctor.surname}`,
    label: `${doctor.name} ${doctor.surname}`,
  }));

  const clinicItems = clinics.map((clinic) => ({
    id: clinic.id,
    value: clinic.name,
    label: clinic.name,
  }));

  const createCart = useMutation(createPatientCartMutation());

  const formik = useFormik<CreateWalkInOrderFormValues>({
    initialValues: {
      name: '',
      customer_firstname: '',
      customer_lastname: '',
      phone: '',
      doctor_id: '',
      clinic_id: '',
    },
    validate: toFormikValidate(createWalkInOrderSchema),
    onSubmit: async (values) => {
      const customer = await createTempCustomer.mutateAsync({
        customer_firstname: values.customer_firstname,
        customer_lastname: values.customer_lastname,
        phone: values.phone,
      });
      const cart = await createCart.mutateAsync({
        user_id: customer.id,
        name: values.name,
        doctor_id: Number(values.doctor_id),
        clinic_id: Number(values.clinic_id),
      });
      formik.resetForm();
      navigate({
        to: routes.dashboard.subscription.cart.items,
        params: { cartId: cart.id },
        search: { walkin: true },
      });
    },
  });

  const createTempCustomer = useFormMutation({
    ...createTempCustomerMutation(),
    formik,
  });

  const isSubmitting = createTempCustomer.isPending || createCart.isPending;

  return (
    <DashboardTableAccordion
      value={ROW_VALUE}
      title="Create walk-in order"
      accordionOpenValue={accordionOpenValue}
      data={[{ id: 1 }]}
      tableWrapper="card"
      customTable={
        <form
          onSubmit={formik.handleSubmit}
          className="flex flex-col gap-4 pt-2.5"
        >
          <Input
            id="name"
            name="name"
            label="Cart name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.name}
            touched={formik.touched.name}
          />

          <div className="flex flex-col gap-4 md:flex-row">
            <Input
              id="customer_firstname"
              name="customer_firstname"
              label="Customer name"
              value={formik.values.customer_firstname}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.customer_firstname}
              touched={formik.touched.customer_firstname}
            />
            <Input
              id="customer_lastname"
              name="customer_lastname"
              label="Customer surname"
              value={formik.values.customer_lastname}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.customer_lastname}
              touched={formik.touched.customer_lastname}
            />
          </div>

          <PhoneInput
            id="phone"
            name="phone"
            label="Phone number"
            value={formik.values.phone}
            onChange={(value) => formik.setFieldValue('phone', value)}
            onBlur={() => formik.setFieldTouched('phone', true)}
            error={formik.errors.phone}
            touched={formik.touched.phone}
          />

          <Combobox
            label="Doctor"
            items={doctorItems}
            value={formik.values.doctor_id}
            onChange={(value) => formik.setFieldValue('doctor_id', value ?? '')}
            onBlur={() => formik.setFieldTouched('doctor_id', true)}
            error={formik.errors.doctor_id}
            touched={formik.touched.doctor_id}
            emptyText="No doctors found."
            className="bg-white"
          />

          <Combobox
            label="Clinic"
            items={clinicItems}
            value={formik.values.clinic_id}
            onChange={(value) => formik.setFieldValue('clinic_id', value ?? '')}
            onBlur={() => formik.setFieldTouched('clinic_id', true)}
            error={formik.errors.clinic_id}
            touched={formik.touched.clinic_id}
            emptyText="No clinics found."
            className="bg-white"
          />
        </form>
      }
      footerActions={
        <Button
          variant="primary"
          size="md"
          disabled={isSubmitting}
          onClick={() => formik.submitForm()}
        >
          {isSubmitting ? 'Creating…' : 'Create cart'}
        </Button>
      }
    />
  );
};
